import { useEffect } from 'react';

import {
  AutoNavigateFn,
  FlowContextProvider,
  StepWrapper,
  useAutoNavigate,
  useGraphQLQuery,
} from '@betterment/js-runtime';
import { SuccessFullScreen } from '@betterment/samba-react';
import { ErrorBoundary } from '@betterment/shared-components';
import { Route, Routes, useMatch, useSearchParams } from 'react-router-dom';

import { DirectTradingAccountCreationFlowDocument } from './graphql/query.directTradingAccountCreationFlow.generated';
import { InitialFundingStep } from './steps/initial-funding/InitialFundingStep';
import { IntroductionStep } from './steps/IntroductionStep';
import { InvestingExperienceStep } from './steps/InvestingExperienceStep';
import TermsAndAgreementsStep from './steps/TermsAndAgreementsStep';
import { UserLevelSettingsStep } from './steps/UserLevelSettingsStep';

import { InvestingExperienceLevel, SecuritiesLendingStatus } from '../graphql/schema';
import { IdempotencyProviderWithFeatureRegistry } from '../RetailApp/graphql/idempotency/IdempotencyProviderWithFeatureRegistry';
import FullPageLoader from '../Shared/components/FullPageLoader';
import { urlBuilder } from '../Shared/urlBuilder';

export type DirectTradingFlowStateValues = {
  investingExperience: InvestingExperienceLevel;
  isSecurityLendingEnrolled?: boolean;
  accountId: string | null;
};

const validateUserIsNotSuspended = (isSuspended: boolean, navigate: AutoNavigateFn): void => {
  if (!isSuspended) return;

  void navigate(urlBuilder.appHome.absolute(), { replace: true });
};
const validateProfileSetupRequired = (
  isProfileSetupRequired: boolean,
  productCategory: string | null,
  navigate: AutoNavigateFn
): void => {
  if (!isProfileSetupRequired) return;
  const profileSetupUrl = urlBuilder.appProfileSetupFlow.absolute({
    ...(productCategory && { product_category: productCategory }),
  });
  void navigate(profileSetupUrl, { replace: true });
};

export const shouldShowUserLevelSettingsStep = (status: SecuritiesLendingStatus): boolean => {
  return ![
    SecuritiesLendingStatus.Ineligible,
    SecuritiesLendingStatus.Unknown,
    SecuritiesLendingStatus.Enrolled,
  ].includes(status);
};

export default function Controller() {
  const match = useMatch(`${urlBuilder.appAddAccount()}/*`);
  const returnToPath = urlBuilder.appHome.absolute();
  const navigate = useAutoNavigate();

  const [searchParams] = useSearchParams();
  const productCategory = searchParams.get('product_category');

  const { data } = useGraphQLQuery(DirectTradingAccountCreationFlowDocument, {
    throwOnError: true,
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (!data) return;

    validateUserIsNotSuspended(data.user.isSuspended, navigate);
    validateProfileSetupRequired(data.user.isProfileSetupRequired, productCategory, navigate);
  }, [data, productCategory, navigate]);

  if (!data || data.user.isProfileSetupRequired || data.user.isSuspended) return <FullPageLoader />;

  const {
    financialProfile,
    goalCreationOption: { showInvestingExperience },
    userLevelSettings: { securityLending },
  } = data;

  const initialFlowValues = {
    investingExperience: financialProfile?.investingExperience || InvestingExperienceLevel.New,
    accountId: null,
  };

  return (
    <ErrorBoundary fallbackType="full" feature="self_directed_investing" owner="user_engagement">
      <IdempotencyProviderWithFeatureRegistry featureName="createDirectTradingAccount">
        <FlowContextProvider<DirectTradingFlowStateValues>
          basePath={match!.pathnameBase}
          routePath=""
          returnToPath={returnToPath}
          initialValues={initialFlowValues}
        >
          <Routes>
            <Route
              path="/introduction"
              element={
                <StepWrapper name="introduction">{stepProps => <IntroductionStep {...stepProps} />}</StepWrapper>
              }
            />
            {showInvestingExperience && (
              <Route
                path="/investing_experience"
                element={
                  <StepWrapper<DirectTradingFlowStateValues> name="investing_experience">
                    {stepProps => <InvestingExperienceStep {...stepProps} />}
                  </StepWrapper>
                }
              />
            )}
            {shouldShowUserLevelSettingsStep(securityLending.status) && (
              <Route
                path="/user_level_settings"
                element={
                  <StepWrapper<DirectTradingFlowStateValues> name="user_level_settings">
                    {stepProps => <UserLevelSettingsStep {...stepProps} />}
                  </StepWrapper>
                }
              />
            )}
            <Route
              path="/terms_and_agreements"
              element={
                <StepWrapper<DirectTradingFlowStateValues> name="terms_and_agreements">
                  {stepProps => <TermsAndAgreementsStep {...stepProps} />}
                </StepWrapper>
              }
            />
            <Route
              path="/success"
              element={
                <StepWrapper<DirectTradingFlowStateValues> name="success">
                  {stepProps => (
                    <SuccessFullScreen message="Your account setup is complete" onCompletion={stepProps.goToNextStep} />
                  )}
                </StepWrapper>
              }
            />
            <Route
              path="/initial_funding"
              element={
                <StepWrapper<DirectTradingFlowStateValues> name="initial_funding">
                  {stepProps => <InitialFundingStep {...stepProps} />}
                </StepWrapper>
              }
            />
          </Routes>
        </FlowContextProvider>
      </IdempotencyProviderWithFeatureRegistry>
    </ErrorBoundary>
  );
}
